import type { JobCategory } from "@/lib/services/bookings";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export interface PendingOffer {
  offer_id: number;
  booking_id: number;
  vehicle_id?: number | null;
  from_address?: string;
  to_address?: string;
  service_type?: string;
  scheduled_at?: string;
  expires_at?: string;
  priority?: boolean;
  job_category?: JobCategory;
  job_category_label?: string;
  received_at?: string;
}

type PendingOffersStore = {
  offers: PendingOffer[];
  addOffer: (offer: Omit<PendingOffer, "received_at">) => void;
  removeOffer: (offerId: number) => void;
  removeOffersForBooking: (bookingId: number) => void;
  markViewed: (offerId: number) => void;
  clearOffers: () => void;
  currentOffer: () => PendingOffer | null;
};

function isOfferActive(offer: PendingOffer): boolean {
  if (!offer.expires_at) {
    return true;
  }

  return new Date(offer.expires_at).getTime() > Date.now();
}

export const usePendingOffersStore = create<PendingOffersStore>()(
  persist(
    (set, get) => ({
      offers: [],
      addOffer: (offer) => {
        const isActive = isOfferActive(offer);
        if (!isActive) {
          return;
        }

        const exists = get().offers.some(
          (o) => o.offer_id === offer.offer_id || o.booking_id === offer.booking_id,
        );
        if (exists) {
          return;
        }

        set({
          offers: [
            ...get().offers,
            { ...offer, received_at: new Date().toISOString() },
          ],
        });
      },
      removeOffer: (offerId) => {
        set({ offers: get().offers.filter((o) => o.offer_id !== offerId) });
      },
      removeOffersForBooking: (bookingId) => {
        set({
          offers: get().offers.filter((o) => o.booking_id !== bookingId),
        });
      },
      markViewed: (_offerId) => {
        // View ack is sent to API; offer stays until dismissed or suppressed
      },
      clearOffers: () => set({ offers: [] }),
      currentOffer: () => {
        const active = get().offers;

        return active[0] ?? null;
      },
    }),
    {
      name: "pending-offers-storage",
      storage: createJSONStorage(() => AsyncStorage),
      partialize: (state) => ({
        offers: state.offers,
      }),
    },
  ),
);
